import { useGLFlags } from '@group-link-one/gl-utils';
import { SPLoading } from '@group-link-one/gl-ui/sonarprint';
import type { JSX } from 'react';
import { Suspense } from 'react';
import { Navigate } from 'react-router-dom';

import { BlockedScreenPage } from '../pages';
import { useAuth } from '@/Bridges/GLUtils';

interface VerifyRouteProps {
  needAuth?: boolean;
  element: React.ReactElement;
  userHasPrivileges?: boolean;
  allowBoth?: boolean; // Novo: para rotas acessíveis com/sem auth
}

export const VerifyRoute = ({
  needAuth,
  userHasPrivileges,
  element,
  allowBoth = false,
}: VerifyRouteProps): JSX.Element => {
  const { accessToken, isPending, storeIsReady } = useAuth();
  const { flagsAlreadyLoaded, user, isOrgBlocked } = useGLFlags();

  const isAccessTokenMissing = !accessToken;
  const isLoading = isPending || !flagsAlreadyLoaded || !user || !storeIsReady;

  if (!flagsAlreadyLoaded) return <SPLoading screen='as-full-screen' type='dots' />;

  // allowBoth: rotas acessíveis com ou sem autenticação
  if (allowBoth) {
    if (!flagsAlreadyLoaded) return <SPLoading screen='as-full-screen' />;
    if (!userHasPrivileges) return <Navigate to='/' replace />;
    return element;
  }

  // Rotas protegidas (needAuth = true)
  if (needAuth) {
    if (isAccessTokenMissing && storeIsReady) {
      return <Navigate to='/' replace />;
    }

    if (isLoading) {
      return <SPLoading screen='as-full-screen' type='dots' />;
    }

    if (isOrgBlocked) {
      return (
        <Suspense fallback={<SPLoading screen='as-full-screen' type='dots' />}>
          <BlockedScreenPage />
        </Suspense>
      );
    }

    // IMPORTANTE: preservar lógica !== true
    if (!userHasPrivileges) {
      return <Navigate to='/' replace />;
    }
  }

  // Rotas públicas (needAuth = false)
  if (!needAuth && !isAccessTokenMissing) {
    return <Navigate to='/' replace />;
  }

  return element;
};
